'use client';

import { useEffect, useRef } from 'react';

import { useReducedMotion } from 'motion/react';

import { cn } from '@/lib/utils';

/** Protocol names and hex, so the rain reads as this product rather than as the film. */
const GLYPHS = 'MCPACPAP2UCPX402{}[]<>/\\|=+-*0123456789abcdef';
const FONT_SIZE = 14;
/** One row per this many ms. Slow enough to read as drifting rather than as static. */
const ROW_MS = 110;
/** How much of the previous frame is erased each row. Sets the trail length. */
const FADE = 0.07;
/** Chance a finished column restarts on any given row. Lower is sparser. */
const RESPAWN = 0.03;

/**
 * Canvas, not DOM or Motion: a column every 14px times a glyph every 14px is thousands of animated
 * nodes, and no library makes that cheap — Motion would be driving thousands of springs off one
 * rAF. Here the whole effect is one element and about ninety `fillText` calls per row.
 *
 * Only the leading glyph is drawn; the trail is the previous frames being erased a slice at a time.
 */
export function MatrixRain({ className }: { className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const reducedMotion = useReducedMotion();

  useEffect(
    function runRain() {
      const canvas = canvasRef.current;
      if (!canvas || reducedMotion) return;
      const ctx = canvas.getContext('2d', { alpha: true });
      if (!ctx) return;

      let columns: number[] = [];
      let ink = '';
      let head = '';

      // Read on a theme change rather than on every row. `getComputedStyle` forces a style
      // recalculation over the whole document, so nine a second was nine document-wide recalcs a
      // second to pick up two values that only move when the toggle is pressed.
      function readTokens(target: HTMLCanvasElement) {
        const styles = getComputedStyle(target);
        ink = styles.getPropertyValue('--rain-ink').trim();
        head = styles.getPropertyValue('--rain-head').trim();
      }
      readTokens(canvas);
      const themeWatcher = new MutationObserver(() => readTokens(canvas));
      themeWatcher.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
      });
      let width = 0;
      let height = 0;
      let rows = 0;
      let frame = 0;
      let last = 0;

      function resize() {
        if (!canvas || !ctx) return;
        const box = canvas.getBoundingClientRect();
        if (box.width < 1 || box.height < 1) return;
        width = box.width;
        height = box.height;
        // Deliberately 1x, not devicePixelRatio: this is a masked, part-opacity backdrop, and
        // rendering it at 2x quadruples the fill area for detail nobody can see through the mask.
        canvas.width = Math.floor(width);
        canvas.height = Math.floor(height);
        ctx.font = `${FONT_SIZE}px ui-monospace, monospace`;
        ctx.textBaseline = 'top';
        rows = Math.ceil(height / FONT_SIZE);
        // Seeded across the full height, so the first painted frame is already raining rather
        // than starting empty and filling in over the next few seconds.
        columns = Array.from({ length: Math.ceil(width / FONT_SIZE) }, function seedColumn() {
          return Math.random() < 0.7 ? Math.floor(Math.random() * rows) : -1;
        });
      }

      function draw(now: number) {
        // Re-scheduled before the throttle check, not after it: rAF is the only clock that stops
        // with the tab, and 60 wake-ups a second to do work 9 times a second is still ~51 no-op
        // callbacks. Cheap as they are, they keep the main thread from ever going idle.
        frame = requestAnimationFrame(draw);
        if (now - last < ROW_MS || !rows || !ctx || !canvas) return;
        last = now;

        // Erase a slice of what is already there. destination-out fades toward transparent, which
        // a translucent fill cannot do on a canvas that has to stay see-through.
        ctx.globalCompositeOperation = 'destination-out';
        ctx.fillStyle = `rgba(0, 0, 0, ${FADE})`;
        ctx.fillRect(0, 0, width, height);
        ctx.globalCompositeOperation = 'source-over';

        columns.forEach(function stepColumn(y, index) {
          if (y < 0) {
            if (Math.random() < RESPAWN) columns[index] = 0;
            return;
          }
          ctx.fillStyle = Math.random() < 0.12 ? head : ink;
          ctx.fillText(
            GLYPHS[Math.floor(Math.random() * GLYPHS.length)],
            index * FONT_SIZE,
            y * FONT_SIZE,
          );
          columns[index] = y > rows ? -1 : y + 1;
        });
      }

      // No visibility or intersection gate: the browser already stops serving rAF to a hidden tab,
      // which is the case that costs anything. Gating on top of that only adds a way for the
      // effect to stop dead and never come back — an IntersectionObserver whose *first* callback
      // reports not-intersecting kills a full-bleed canvas that then never crosses again.
      const size = new ResizeObserver(resize);
      resize();
      size.observe(canvas);
      frame = requestAnimationFrame(draw);

      return function teardown() {
        cancelAnimationFrame(frame);
        size.disconnect();
        themeWatcher.disconnect();
      };
    },
    [reducedMotion],
  );

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={cn(
        'pointer-events-none absolute inset-0 size-full',
        // The canvas reads these; nothing paints them.
        '[--rain-head:var(--color-brand)] [--rain-ink:var(--color-muted-foreground)]',
        className,
      )}
    />
  );
}
